import { memo, type ReactNode } from "react";
import { formatDistanceToNow } from "date-fns";
import { ExternalLink, FileText, Folder, Lock, MoreHorizontal, Shield } from "lucide-react";
import { EmbeddedSearchMark } from "@/components/EmbeddedSearchMark";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  DocumentActionsMenuContent,
  type DocumentActionsItem,
} from "@/components/DocumentActionsMenuContent";
import type { LibraryDocumentActions } from "@/components/LibraryDocumentRow";

export type LibraryBrowseDocumentRowProps = {
  item: DocumentActionsItem;
  viewMode: "grid" | "list";
  typeIcon: ReactNode;
  typeLabel: string;
  folderName?: string | null;
  updatedAt: string;
  embeddingStatusMap?: Record<string, boolean>;
  documentActions: LibraryDocumentActions;
  onOpen: () => void;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  isSourceOfTruth?: boolean;
};

function BrowseDocumentMenu({
  item,
  embeddingStatusMap,
  documentActions,
  isFavorite,
  onToggleFavorite,
  isSourceOfTruth,
  triggerClassName,
}: {
  item: DocumentActionsItem;
  embeddingStatusMap?: Record<string, boolean>;
  documentActions: LibraryDocumentActions;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  isSourceOfTruth?: boolean;
  triggerClassName?: string;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <Button
          variant="ghost"
          size="icon"
          className={triggerClassName ?? "h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100"}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DocumentActionsMenuContent
        item={item}
        isEmbedded={embeddingStatusMap?.[item.id]}
        isFavorite={isFavorite}
        onToggleFavorite={
          onToggleFavorite
            ? () => onToggleFavorite()
            : undefined
        }
        onEdit={documentActions.onEdit}
        onRename={documentActions.onRename}
        onDuplicate={documentActions.onDuplicate}
        onMerge={documentActions.onMerge}
        onManageLinks={documentActions.onManageLinks}
        onReembedForSearch={documentActions.onReembedForSearch}
        onEmbedForSearch={documentActions.onEmbedForSearch}
        onRemoveFromSearch={documentActions.onRemoveFromSearch}
        onToggleIncludeInRag={documentActions.onToggleIncludeInRag}
        onMoveToFolder={documentActions.onMoveToFolder}
        onRemoveFromFolder={documentActions.onRemoveFromFolder}
        onDelete={documentActions.onDelete}
        onOpenLink={documentActions.onOpenLink}
        onViewWorks={documentActions.onViewWorks}
        isSourceOfTruth={isSourceOfTruth}
        onToggleSourceOfTruth={documentActions.onToggleSourceOfTruth}
        onCopyPublicLink={documentActions.onCopyPublicLink}
        showCopyPublicLink={
          documentActions.showCopyPublicLink === true && item.itemType === "note"
        }
        stopPropagation={true}
      />
    </DropdownMenu>
  );
}

/** Main library panel browse row — no per-row queries (unlike DocumentItem). */
export const LibraryBrowseDocumentRow = memo(function LibraryBrowseDocumentRow({
  item,
  viewMode,
  typeIcon,
  typeLabel,
  folderName,
  updatedAt,
  embeddingStatusMap,
  documentActions,
  onOpen,
  isFavorite,
  onToggleFavorite,
  isSourceOfTruth = false,
}: LibraryBrowseDocumentRowProps) {
  const updatedLabel = formatDistanceToNow(new Date(updatedAt), { addSuffix: true });

  if (viewMode === "list") {
    return (
      <div
        className="flex items-center gap-3 min-w-0 w-full cursor-pointer group py-1"
        onClick={onOpen}
      >
        <div className="flex items-center gap-1.5 shrink-0">{typeIcon}</div>
        <Badge variant="secondary" className="text-xs px-1.5 py-0.5 whitespace-nowrap shrink-0">
          {typeLabel}
        </Badge>
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <span className="font-medium text-sm truncate hover:text-primary transition-colors">
            {item.title}
          </span>
          {embeddingStatusMap?.[item.id] && <EmbeddedSearchMark />}
          {item.isLocked && (
            <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          )}
          {isSourceOfTruth && (
            <Badge variant="outline" className="text-[10px] h-5 gap-0.5 shrink-0" title="Source of Truth">
              <Shield className="h-3 w-3" />
              Canon
            </Badge>
          )}
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0 hidden md:inline">
          {updatedLabel}
        </span>
        <BrowseDocumentMenu
          item={item}
          embeddingStatusMap={embeddingStatusMap}
          documentActions={documentActions}
          isFavorite={isFavorite}
          onToggleFavorite={onToggleFavorite}
          isSourceOfTruth={isSourceOfTruth}
          triggerClassName="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100"
        />
      </div>
    );
  }

  return (
    <Card
      className="group hover:shadow-md transition-shadow cursor-pointer h-full"
      onClick={onOpen}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {typeIcon}
            <Badge variant="secondary" className="text-xs shrink-0">
              {typeLabel}
            </Badge>
            {item.isLocked && (
              <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            )}
          </div>
          <BrowseDocumentMenu
            item={item}
            embeddingStatusMap={embeddingStatusMap}
            documentActions={documentActions}
            isFavorite={isFavorite}
            onToggleFavorite={onToggleFavorite}
            isSourceOfTruth={isSourceOfTruth}
          />
        </div>
        {folderName && (
          <div className="mb-2">
            <Badge
              variant="outline"
              className="text-xs gap-1 bg-amber-50 border-amber-200 text-amber-700"
            >
              <Folder className="h-3 w-3" />
              {folderName}
            </Badge>
          </div>
        )}
        <h3 className="font-medium text-sm line-clamp-2 mb-2 inline-flex items-start gap-1.5">
          <span className="line-clamp-2">{item.title}</span>
          {embeddingStatusMap?.[item.id] && <EmbeddedSearchMark className="mt-0.5" />}
        </h3>
        {item.url && item.itemType === "reference" && (
          <span
            className="text-xs text-primary flex items-center gap-1 mb-2"
            onClick={(e) => {
              e.stopPropagation();
              window.open(item.url || undefined, "_blank");
            }}
          >
            <ExternalLink className="h-3 w-3" />
            Open link
          </span>
        )}
        <p className="text-xs text-muted-foreground">{updatedLabel}</p>
      </CardContent>
    </Card>
  );
});

LibraryBrowseDocumentRow.displayName = "LibraryBrowseDocumentRow";

/** Table view: actions menu only (cells rendered by Library.tsx). */
export const LibraryBrowseDocumentMenu = memo(function LibraryBrowseDocumentMenu({
  item,
  embeddingStatusMap,
  documentActions,
  isFavorite,
  onToggleFavorite,
}: {
  item: DocumentActionsItem;
  embeddingStatusMap?: Record<string, boolean>;
  documentActions: LibraryDocumentActions;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}) {
  return (
    <BrowseDocumentMenu
      item={item}
      embeddingStatusMap={embeddingStatusMap}
      documentActions={documentActions}
      isFavorite={isFavorite}
      onToggleFavorite={onToggleFavorite}
      triggerClassName="h-8 w-8"
    />
  );
});

LibraryBrowseDocumentMenu.displayName = "LibraryBrowseDocumentMenu";
